import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Search, 
  Clock, 
  User, 
  ArrowRight, 
  Tag, 
  CheckCircle2, 
  ShieldCheck, 
  ChevronRight,
  Filter,
  Calendar,
  Sparkles,
  Layers
} from 'lucide-react';
import { RESOURCE_ARTICLES } from '../data/resources';
import { ResourceArticle } from '../types';

export const ResourcesPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [publicationFilter, setPublicationFilter] = useState<'all' | 'published' | 'scheduled'>('all');

  const categories = [
    'All',
    'Australian ID Protection',
    'Credit Fraud',
    'Scams',
    'Phishing',
    'Password Security',
    'Identity Theft',
    'Data Breaches',
    'Online Shopping'
  ];

  // Current reference date: 20 August 2026
  const isFutureDate = (dateStr: string) => {
    try {
      const parsed = new Date(dateStr);
      const refDate = new Date('2026-08-20T23:59:59');
      return parsed > refDate;
    } catch {
      return false;
    }
  };

  const filteredArticles = RESOURCE_ARTICLES.filter((art) => {
    if (selectedCategory !== 'All' && art.category !== selectedCategory) return false;
    
    const isFuture = isFutureDate(art.date);
    if (publicationFilter === 'published' && isFuture) return false;
    if (publicationFilter === 'scheduled' && !isFuture) return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        art.title.toLowerCase().includes(q) ||
        art.summary.toLowerCase().includes(q) ||
        art.keyTakeaways.some(t => t.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const publishedCount = RESOURCE_ARTICLES.filter(a => !isFutureDate(a.date)).length;
  const scheduledCount = RESOURCE_ARTICLES.filter(a => isFutureDate(a.date)).length;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      {/* Header */}
      <div className="text-center space-y-3 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-950 text-emerald-300 text-xs font-bold border border-emerald-800">
          <BookOpen className="w-3.5 h-3.5" />
          <span>AUSTRALIAN IDENTITY FRAUD KNOWLEDGE BASE & EDITORIAL CALENDAR</span>
        </div>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
          130 Expert Security Playbooks & Guides
        </h1>
        <p className="text-slate-400 text-sm leading-relaxed">
          Australia's most comprehensive identity protection repository. Featuring {publishedCount} published master playbooks and {scheduledCount} scheduled upcoming editorial guides releasing every 2–4 days.
        </p>
      </div>

      {/* Search, Filter & Schedule Selector Bar */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4 shadow-xl">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search all 130 playbooks (e.g. credit ban, driver licence, PayID, myGov, SIM swap, superannuation)..."
            className="w-full pl-11 pr-4 py-3 rounded-2xl bg-slate-950 border border-slate-700 text-white placeholder-slate-500 text-xs sm:text-sm focus:outline-none focus:border-emerald-500 shadow-inner"
          />
        </div>

        {/* Publication Status Selector */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 font-semibold flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-emerald-400" />
              <span>Release Schedule:</span>
            </span>
            <div className="flex gap-1.5">
              <button
                onClick={() => setPublicationFilter('all')}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                  publicationFilter === 'all'
                    ? 'bg-emerald-600 text-white shadow'
                    : 'bg-slate-950 text-slate-400 hover:text-white border border-slate-800'
                }`}
              >
                All Guides ({RESOURCE_ARTICLES.length})
              </button>
              <button
                onClick={() => setPublicationFilter('published')}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                  publicationFilter === 'published'
                    ? 'bg-emerald-600 text-white shadow'
                    : 'bg-slate-950 text-slate-400 hover:text-white border border-slate-800'
                }`}
              >
                Active Published ({publishedCount})
              </button>
              <button
                onClick={() => setPublicationFilter('scheduled')}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                  publicationFilter === 'scheduled'
                    ? 'bg-purple-600 text-white shadow'
                    : 'bg-slate-950 text-slate-400 hover:text-white border border-slate-800'
                }`}
              >
                Scheduled Releases (Every 2–4 Days) ({scheduledCount})
              </button>
            </div>
          </div>

          <span className="text-xs text-slate-400 font-mono">
            Showing {filteredArticles.length} Playbooks
          </span>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-1.5 pt-1">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                selectedCategory === cat
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'bg-slate-950 text-slate-300 hover:text-white border border-slate-800'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredArticles.map((article) => {
          const isFuture = isFutureDate(article.date);
          return (
            <div
              key={article.id}
              className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl flex flex-col justify-between hover:border-slate-700 hover:bg-slate-900/90 transition-all group"
            >
              <div>
                {/* Card Image Thumbnail */}
                {article.imageUrl && (
                  <Link to={`/resources/${article.slug}`} className="block relative overflow-hidden h-48">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-slate-950/80 backdrop-blur-md text-[10px] font-bold text-emerald-400 border border-slate-800 uppercase">
                      {article.category}
                    </div>

                    {isFuture && (
                      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-purple-950/90 backdrop-blur-md text-[10px] font-bold text-purple-300 border border-purple-800 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>Releasing {article.date}</span>
                      </div>
                    )}
                  </Link>
                )}

                <div className="p-6 space-y-3">
                  <div className="flex items-center justify-between text-[11px] text-slate-400 font-mono">
                    <span className={isFuture ? 'text-purple-400 font-semibold' : ''}>
                      {isFuture ? `Scheduled: ${article.date}` : article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-slate-500" /> {article.readingTimeMinutes} min read
                    </span>
                  </div>

                  <h3 className="text-base font-bold text-white group-hover:text-emerald-400 transition-colors leading-snug">
                    <Link to={`/resources/${article.slug}`}>{article.title}</Link>
                  </h3>

                  <p className="text-xs text-slate-300 line-clamp-3 leading-relaxed">
                    {article.summary}
                  </p>
                </div>
              </div>

              <div className="px-6 pb-6 pt-2 border-t border-slate-800/80 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <img
                    src={article.author.avatar}
                    alt={article.author.name}
                    className="w-5 h-5 rounded-full object-cover border border-slate-700"
                  />
                  <span className="text-slate-400 font-medium text-[11px]">{article.author.name}</span>
                </div>

                <Link
                  to={`/resources/${article.slug}`}
                  className="font-bold text-emerald-400 hover:text-emerald-300 flex items-center gap-1 group-hover:translate-x-1 transition-transform"
                >
                  <span>{isFuture ? 'Preview Guide' : 'Read Guide'}</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Conversion CTA Banner */}
      <div className="p-8 sm:p-10 rounded-3xl bg-gradient-to-r from-emerald-950 via-slate-900 to-slate-900 border border-emerald-800/80 text-center space-y-4 shadow-2xl">
        <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
          Ready to put these safeguards into action?
        </h3>
        <p className="text-slate-300 text-xs sm:text-sm max-w-lg mx-auto">
          Sign up for IDFraud.au to activate automated breach detection, watermark your ID documents, and monitor your Australian identity 24/7.
        </p>
        <div className="pt-2 flex flex-wrap justify-center gap-3">
          <Link
            to="/check-identity"
            className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-emerald-950/40 transition-colors"
          >
            Check My Identity Free
          </Link>
          <Link
            to="/pricing"
            className="px-6 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 font-semibold text-xs sm:text-sm transition-colors"
          >
            Explore Plans & Pricing
          </Link>
        </div>
      </div>
    </div>
  );
};
